import { z } from 'zod';
import { offerRequestEstimatedVolumeSchema } from '../../offer-request-management/projectSize.js';
import { contactAttemptCountSchema, REIMBURSEMENT_REASONS, reimbursementReasonSchema, } from '../../offer-request-management/reimbursement.js';
// Absent on a lead with nothing to reimburse: no assignment behind it, or no credits were deducted.
// `Requestable` only reflects that a request may exist — the per-reason eligibility rules are
// enforced when the request is submitted.
export const partnerCockpitReimbursementStateSchema = z.enum([
    'Requestable',
    'Requested',
    'Approved',
    'Declined',
]);
// Re-exported under the cockpit's own names so its call sites read consistently; the values live with
// the offer request domain that owns them.
export const partnerCockpitReimbursementReasonSchema = reimbursementReasonSchema;
export const PARTNER_COCKPIT_REIMBURSEMENT_REASONS = REIMBURSEMENT_REASONS;
// Why a reimbursement cannot be requested. `AlreadyRequested` also covers a decided request, since an
// assignment only ever carries one partner-initiated one.
export const partnerCockpitReimbursementBlockerSchema = z.enum([
    'NotReimbursable',
    'NeverOpened',
    'OpenedTooLate',
    'AlreadyRequested',
]);
export const partnerCockpitReimbursementEligibilitySchema = z.object({
    state: partnerCockpitReimbursementStateSchema.nullable(),
    canRequest: z.boolean(),
    blocker: partnerCockpitReimbursementBlockerSchema.nullable(),
    // Credits at stake on the assignment behind this lead; null when nothing was deducted.
    credits: z.number().int().nullable(),
    // Reasons the partner may pick right now. A reason can be withheld even on an otherwise eligible
    // lead, because some of them only become true after enough time has passed.
    availableReasons: z.array(partnerCockpitReimbursementReasonSchema),
});
// The window a partner has to open a lead before it stops being reimbursable.
export const REIMBURSEMENT_OPENING_WINDOW_BUSINESS_HOURS = 48;
export const partnerCockpitContactAttemptCountSchema = contactAttemptCountSchema;
const partnerFeedback = z.string().trim().min(1).max(2000);
const proofDocumentsUuids = z.array(z.uuid());
const contactChannels = {
    contactedByPhone: z.boolean(),
    contactedByEmail: z.boolean(),
    contactedBySmsOrWhatsApp: z.boolean(),
};
// Mirrors the reason-specific evidence the ops-tool flow collects, so both surfaces feed partner
// management the same shape.
export const partnerCockpitReimbursementRequestSchema = z.discriminatedUnion('reason', [
    z.object({
        reason: z.literal('TooManyCreditsBilled'),
        partnerFeedback,
        proofDocumentsUuids,
        estimatedVolume: offerRequestEstimatedVolumeSchema.exclude(['InformationOnly']),
    }),
    z.object({ reason: z.literal('CannotDoThisWork'), partnerFeedback }),
    z.object({
        reason: z.literal('NotEnoughCapacityOrOnHoliday'),
        partnerFeedback,
        absencePeriod: z.object({ from: z.iso.date(), to: z.iso.date() }),
    }),
    z.object({ reason: z.literal('UserIsTooFarAway'), partnerFeedback }),
    z.object({
        reason: z.literal('UserNotReachable'),
        partnerFeedback,
        proofDocumentsUuids,
        ...contactChannels,
        numberOfTimesUserWasContacted: partnerCockpitContactAttemptCountSchema,
    }),
    z.object({
        reason: z.literal('UserNotInterested'),
        partnerFeedback,
        proofDocumentsUuids,
        ...contactChannels,
    }),
    z.object({ reason: z.literal('LanguageBarrier'), partnerFeedback, proofDocumentsUuids }),
]);
// Wrapped in an object because a request body must be one, and a class cannot extend a union type.
export const partnerCockpitReimbursementRequestBodySchema = z.object({
    request: partnerCockpitReimbursementRequestSchema,
});
export const REIMBURSEMENT_PROOF_MAX_BYTES = 10 * 1024 * 1024;
export const REIMBURSEMENT_PROOF_MAX_COUNT = 5;
export const REIMBURSEMENT_PROOF_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'application/pdf',
];
export const reimbursementProofUploadSchema = z.object({
    type: z.enum(REIMBURSEMENT_PROOF_MIME_TYPES),
    size: z.number().int().positive().max(REIMBURSEMENT_PROOF_MAX_BYTES),
});
export const reimbursementProofDocumentSchema = z.object({
    uuid: z.uuid(),
    displayName: z.string(),
    mimeType: z.string(),
});
// Reasons that ask the partner for evidence before partner management will decide them.
export const REASONS_REQUIRING_PROOF = [
    'TooManyCreditsBilled',
    'UserNotReachable',
    'UserNotInterested',
];
export function reimbursementReasonRequiresProof(reason) {
    return REASONS_REQUIRING_PROOF.includes(reason);
}
// UserNotReachable only becomes available once the partner has had time to actually try.
export const USER_NOT_REACHABLE_MIN_DAYS_SINCE_DELIVERY = 7;
